/**
 * Theme toggle (§13.1: "A light theme exists; dark is default").
 *
 * The stored preference stamps data-theme on <html>, which must beat the
 * prefers-color-scheme media query in both directions.
 *
 * One store for the whole app, not state per hook call: the sidebar, the mobile
 * bar and the ⌘K palette all toggle the theme, and three independent copies of
 * it would disagree about which way the next toggle goes.
 */

import { useCallback, useSyncExternalStore } from "react";

type Theme = "dark" | "light";
const KEY = "mydeck.theme";

function readStored(): Theme | null {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    return null;
  }
}

function resolveInitial(): Theme {
  if (typeof window === "undefined") return "dark";
  return (
    readStored() ??
    (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark")
  );
}

let current: Theme = resolveInitial();
const listeners = new Set<() => void>();

function apply(theme: Theme) {
  current = theme;
  document.documentElement.setAttribute("data-theme", theme);
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Private mode or blocked storage: the theme still applies for this visit.
  }
  listeners.forEach((l) => l());
}

if (typeof document !== "undefined") {
  document.documentElement.setAttribute("data-theme", current);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => current);
  const setTheme = useCallback((t: Theme) => apply(t), []);
  const toggle = useCallback(() => apply(current === "dark" ? "light" : "dark"), []);
  return { theme, setTheme, toggle };
}
